// «Ταμείο → Κινήσεις ταμείου» στη διαχείριση καταστήματος (Φάση 2, διάταξη «Πίνακας»): όλες οι κινήσεις
// μετρητών του συρταριού (έξοδα, αναλήψεις, καταθέσεις) με φίλτρα περιόδου (μέρες Ελλάδας), είδους, κατηγορίας
// και κατάστασης· από πάνω τα σύνολα ΟΛΟΥ του φίλτρου, ανά είδος και ανά κατηγορία εξόδου. Οι ακυρωμένες
// φαίνονται σημαδεμένες (ποιος, λόγος) και δεν μετρούν. «Εξαγωγή CSV» για τον λογιστή με τα ίδια φίλτρα, και
// «Κατηγορίες εξόδων» (ExpenseCategoriesPanel). Μόνο ανάγνωση: η καταχώριση και η ακύρωση γίνονται στο «Ταμείο».
// Backend: routes/cashMovements.js (πύλη: διαχειριστής ή χρήστης οργανισμού).

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, RefreshCw, Tags, Wallet } from 'lucide-react';
import { useAuth } from '../../../hooks/useAuth';
import { saveBlob } from '../../../services/api';
import { LoadFailed } from '../../settings/instantSettings';
import {
  Column, DataTable, DateRange, DateRangeValue, DetailPanel, EmptyState, FilterSelect, GHOST_BTN, KeyValue, KeyValueItem,
  ListPage, ListToolbar, Notice, Pagination, Stat, Stats, StatusChip, presetRange,
} from '../../shared/listPage';
import type { CashMovement, CashMovementKind } from '../../../types/registerReport';
import { euro, signedEuro } from '../registerFormat';
import {
  cashMovementsApi, CashMovementApiError, type AdminCategories, type CashMovementsFilters, type CashMovementsPage,
  type CashMovementStatus,
} from './api';
import ExpenseCategoriesPanel from './ExpenseCategoriesPanel';
import { KIND_META, KIND_ORDER, drawerSign } from './model';
import { KindIcon, KIND_TONE } from './ui';

const PAGE_SIZE = 50;
const ATHENS = 'Europe/Athens';

const dateOf = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('el-GR', { timeZone: ATHENS, day: '2-digit', month: '2-digit', year: 'numeric' })
  : '—');
const timeOf = (iso: string | null) => (iso
  ? new Date(iso).toLocaleTimeString('el-GR', { timeZone: ATHENS, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
  : '--:--');
const whenOf = (iso: string | null) => `${dateOf(iso)} · ${timeOf(iso)}`;
const signed = (m: CashMovement) => `${drawerSign(m) > 0 ? '+' : '−'}${euro(m.amount)}`;
const supplierOf = (m: CashMovement) => [m.supplierName, m.supplierVat ? `ΑΦΜ ${m.supplierVat}` : null,
  m.documentNumber ? `Αρ. ${m.documentNumber}` : null].filter(Boolean).join(' · ');

const STATUS_OPTIONS: Array<{ value: Exclude<CashMovementStatus, ''>; label: string }> = [
  { value: 'active', label: 'Μόνο ενεργές' },
  { value: 'voided', label: 'Μόνο ακυρωμένες' },
];

type LoadState = 'ready' | 'error' | 'forbidden';

const CashMovementsAdminPage: React.FC = () => {
  const { t } = useTranslation(['settings', 'listPage']);
  const { store } = useAuth();
  const storeId = store?.id;
  const [range, setRange] = useState<DateRangeValue>(() => presetRange('last30'));
  const [kind, setKind] = useState<'' | CashMovementKind>('');
  const [categoryId, setCategoryId] = useState('');
  const [status, setStatus] = useState<CashMovementStatus>('');
  const [data, setData] = useState<CashMovementsPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState<LoadState>('ready');
  const [selected, setSelected] = useState<CashMovement | null>(null);
  const [categories, setCategories] = useState<AdminCategories | null>(null);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // Κάθε αλλαγή φίλτρου στέλνει αίτημα: μετρά μόνο η απάντηση του τελευταίου.
  const seq = useRef(0);

  // Κατηγορία μόνο στα έξοδα: με «Ανάληψη»/«Κατάθεση» το φίλτρο κατηγορίας δεν στέλνεται.
  const categoryUsable = kind === '' || kind === 'expense';
  const filters = useMemo<CashMovementsFilters>(() => ({
    from: range.from, to: range.to, kind, categoryId: categoryUsable ? categoryId : '', status,
  }), [range.from, range.to, kind, categoryId, categoryUsable, status]);

  const load = useCallback(async (page = 1) => {
    if (!storeId) return;
    const id = ++seq.current;
    setLoading(true);
    try {
      const out = await cashMovementsApi.list(storeId, filters, { page, limit: PAGE_SIZE });
      if (id !== seq.current) return;
      setData(out);
      setState('ready');
    } catch (e) {
      if (id !== seq.current) return;
      setState(e instanceof CashMovementApiError && e.status === 403 ? 'forbidden' : 'error');
    } finally {
      if (id === seq.current) setLoading(false);
    }
  }, [storeId, filters]);

  useEffect(() => {
    load(1);
  }, [load]);

  const loadCategories = useCallback(async () => {
    if (!storeId) return;
    try {
      setCategories(await cashMovementsApi.categories(storeId));
    } catch {
      setCategories(null);
    }
  }, [storeId]);

  useEffect(() => {
    loadCategories();
  }, [loadCategories]);

  const filtersActive = !!(kind || categoryId || status);
  const clearFilters = () => {
    setKind('');
    setCategoryId('');
    setStatus('');
  };

  const exportCsv = async () => {
    if (!storeId || exporting) return;
    setExporting(true);
    setExportError(null);
    try {
      const { blob, fileName } = await cashMovementsApi.exportCsv(storeId, filters);
      saveBlob(blob, fileName);
    } catch (e) {
      setExportError(e instanceof Error && e.message ? e.message : 'Η εξαγωγή απέτυχε');
    } finally {
      setExporting(false);
    }
  };

  const categoryOptions = useMemo(() => (categories?.categories || []).map(c => ({
    value: String(c.id), label: c.active ? c.name : `${c.name} (ανενεργή)`,
  })), [categories]);

  const columns = useMemo<Column<CashMovement>[]>(() => [
    {
      key: 'at',
      header: 'Ημερομηνία',
      width: 'minmax(150px, 1fr)',
      render: m => <span className="tabular-nums">{whenOf(m.at)}</span>,
      stackRender: m => whenOf(m.at),
    },
    {
      key: 'kind',
      header: 'Είδος',
      width: '132px',
      stack: 'title',
      render: m => (
        <span className={`inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[12.5px] font-semibold ${KIND_TONE[m.kind].badge}`}>
          <KindIcon kind={m.kind} className="h-3.5 w-3.5" />
          {KIND_META[m.kind].label}
        </span>
      ),
      stackRender: m => `${KIND_META[m.kind].label}${m.category ? ` · ${m.category}` : ''}`,
    },
    {
      key: 'what',
      header: 'Κατηγορία / σημείωση',
      width: 'minmax(170px, 1.8fr)',
      stack: 'meta',
      render: m => (
        <>
          {m.category && <span className="block font-medium text-t1">{m.category}</span>}
          {m.note && <span className="block text-sm text-t2">{m.note}</span>}
          {supplierOf(m) && <span className="block text-xs text-t3">{supplierOf(m)}</span>}
          {!m.category && !m.note && !supplierOf(m) && <span className="text-t4">—</span>}
        </>
      ),
      stackRender: m => m.note || supplierOf(m) || null,
    },
    {
      key: 'amount',
      header: 'Ποσό',
      width: '120px',
      align: 'right',
      stack: 'end',
      render: m => (
        <span className={`font-semibold tabular-nums ${m.voidedAt ? 'text-t3 line-through' : KIND_TONE[m.kind].amount}`}>{signed(m)}</span>
      ),
      stackRender: m => signed(m),
    },
    {
      key: 'who',
      header: 'Χρήστης',
      width: 'minmax(120px, 1fr)',
      hideBelow: 'medium',
      render: m => <span className="text-t2">{m.who}</span>,
    },
    {
      key: 'status',
      header: 'Κατάσταση',
      width: '128px',
      render: m => (m.voidedAt ? <StatusChip tone="danger">Ακυρώθηκε</StatusChip> : null),
      stackRender: m => (m.voidedAt ? 'Ακυρώθηκε' : null),
    },
  ], []);

  const detailItems = (m: CashMovement): KeyValueItem[] => [
    { id: 'kind', label: 'Είδος', value: KIND_META[m.kind].label },
    { id: 'amount', label: 'Ποσό', value: <span className="tabular-nums">{signed(m)}</span>, hint: m.voidedAt ? 'Ακυρωμένη — δεν μετρά' : undefined },
    ...(m.kind === 'expense' ? [{ id: 'category', label: 'Κατηγορία', value: m.category }] : []),
    { id: 'at', label: 'Ημερομηνία', value: whenOf(m.at) },
    { id: 'who', label: 'Χρήστης', value: m.who },
    { id: 'note', label: 'Σημείωση', value: m.note },
    ...(m.kind === 'expense' ? [
      { id: 'supplier', label: 'Προμηθευτής', value: m.supplierName },
      { id: 'vat', label: 'ΑΦΜ προμηθευτή', value: m.supplierVat },
      { id: 'doc', label: 'Αρ. παραστατικού', value: m.documentNumber },
    ] : []),
    {
      id: 'status',
      label: 'Κατάσταση',
      value: m.voidedAt ? <StatusChip tone="danger">Ακυρώθηκε</StatusChip> : <StatusChip tone="ok">Ενεργή</StatusChip>,
      hint: m.voidedAt ? `από ${m.voidedBy || 'Χωρίς χρήστη'} στις ${whenOf(m.voidedAt)}${m.voidReason ? ` — ${m.voidReason}` : ''}` : undefined,
    },
  ];

  const totals = data?.totals;
  const firstLoad = loading && !data;

  return (
    <ListPage
      eyebrow={t('settings:nav.groups.register')}
      title={t('settings:nav.cashMovements')}
      subtitle="Έξοδα, αναλήψεις και καταθέσεις μετρητών από το συρτάρι. Οι ακυρωμένες φαίνονται, αλλά δεν μετρούν στα σύνολα."
      actions={(
        <button type="button" className={GHOST_BTN} onClick={() => setCategoriesOpen(true)} disabled={!categories}
          data-testid="cash-admin-categories">
          <Tags className="w-4 h-4" aria-hidden="true" />
          Κατηγορίες εξόδων
        </button>
      )}
      testId="cash-movements-admin"
    >
      {store?.cash_movements_enabled !== true && (
        <Notice testId="cash-admin-disabled">
          Οι κινήσεις ταμείου είναι κλειστές (Ρυθμίσεις → «Ζ και Χ»): το ταμείο δεν καταχωρεί νέες. Εδώ φαίνονται όσες καταγράφηκαν.
        </Notice>
      )}

      <ListToolbar
        filters={(
          <>
            <DateRange from={range.from} to={range.to} onChange={setRange} />
            <FilterSelect label="Είδος" value={kind} allLabel="Όλα τα είδη" testId="cash-filter-kind"
              onChange={v => setKind(v as '' | CashMovementKind)}
              options={KIND_ORDER.map(k => ({ value: k, label: KIND_META[k].label }))} />
            {categoryOptions.length > 0 && (
              <FilterSelect label="Κατηγορία εξόδου" value={categoryUsable ? categoryId : ''} allLabel="Όλες οι κατηγορίες"
                disabled={!categoryUsable} testId="cash-filter-category" onChange={setCategoryId} options={categoryOptions} />
            )}
            <FilterSelect label="Κατάσταση" value={status} allLabel="Ενεργές και ακυρωμένες" testId="cash-filter-status"
              onChange={v => setStatus(v as CashMovementStatus)} options={STATUS_OPTIONS} />
            {filtersActive && (
              <button type="button" className={GHOST_BTN} onClick={clearFilters}>Καθαρισμός</button>
            )}
          </>
        )}
        actions={(
          <>
            <button type="button" className={GHOST_BTN} onClick={exportCsv} disabled={exporting || state !== 'ready'}
              data-testid="cash-admin-export" title="CSV για τον λογιστή, με τα ίδια φίλτρα">
              <Download className="w-4 h-4" aria-hidden="true" />
              {exporting ? 'Εξαγωγή…' : 'Εξαγωγή CSV'}
            </button>
            <button type="button" className={GHOST_BTN} onClick={() => load(data?.pagination.page || 1)} disabled={loading}>
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
              Ανανέωση
            </button>
          </>
        )}
        summary={state === 'ready' && data && !firstLoad
          ? `${data.pagination.total} ${data.pagination.total === 1 ? 'κίνηση' : 'κινήσεις'}`
          : null}
      />

      {exportError && <Notice tone="danger" testId="cash-admin-export-error">{exportError}</Notice>}

      {state === 'ready' && totals && (
        <div className="grid gap-1" data-testid="cash-admin-summary">
          <Stats testId="cash-admin-totals">
            <Stat label="Έξοδα" value={<span className={KIND_TONE.expense.amount}>−{euro(totals.expense)}</span>} hint={`(${totals.counts.expense})`} />
            <Stat label="Αναλήψεις" value={<span className={KIND_TONE.withdrawal.amount}>−{euro(totals.withdrawal)}</span>} hint={`(${totals.counts.withdrawal})`} />
            <Stat label="Καταθέσεις" value={<span className={KIND_TONE.deposit.amount}>+{euro(totals.deposit)}</span>} hint={`(${totals.counts.deposit})`} />
            <Stat label="Καθαρή κίνηση συρταριού" value={signedEuro(totals.net)} />
            {totals.voided.count > 0 && (
              <Stat label="Ακυρωμένες" value={totals.voided.count} hint={`(${euro(totals.voided.value)}, δεν μετρούν)`} />
            )}
          </Stats>
          {data.byCategory.length > 0 && (
            <Stats testId="cash-admin-by-category">
              <span className="lp-stat-label">Έξοδα ανά κατηγορία:</span>
              {data.byCategory.map(c => (
                <Stat key={c.categoryId ?? 'none'} label={c.name || 'Χωρίς κατηγορία'} value={euro(c.amount)} hint={`(${c.count})`} />
              ))}
            </Stats>
          )}
        </div>
      )}

      {state !== 'ready' ? (
        <LoadFailed
          message={state === 'forbidden' ? 'Οι κινήσεις ταμείου φαίνονται μόνο στον διαχειριστή.' : 'Δεν φορτώθηκαν οι κινήσεις ταμείου.'}
          onRetry={() => load(data?.pagination.page || 1)}
        />
      ) : (
        <DataTable
          columns={columns}
          rows={data?.items || []}
          rowKey={m => m.id}
          onRowClick={setSelected}
          selectedKey={selected?.id ?? null}
          loading={loading}
          stickyHeader
          rowClassName={m => (m.voidedAt ? 'opacity-60' : undefined)}
          rowLabel={m => `${KIND_META[m.kind].label} ${signed(m)}, ${whenOf(m.at)}`}
          empty={(
            <EmptyState
              icon={Wallet}
              title="Καμία κίνηση ταμείου"
              hint={filtersActive ? 'Δοκίμασε άλλα φίλτρα ή μεγαλύτερη περίοδο.' : 'Οι κινήσεις καταχωρούνται από το «Ταμείο» του ταμείου.'}
            />
          )}
          footer={data && (
            <Pagination
              page={data.pagination.page}
              pageSize={data.pagination.limit || PAGE_SIZE}
              total={data.pagination.total}
              onPage={p => load(p)}
              disabled={loading}
            />
          )}
        />
      )}

      <DetailPanel
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `${KIND_META[selected.kind].label} ${signed(selected)}` : ''}
        subtitle={selected ? whenOf(selected.at) : undefined}
        footer={<button type="button" className={GHOST_BTN} onClick={() => setSelected(null)}>{t('listPage:close')}</button>}
        testId="cash-admin-detail"
      >
        {selected && <KeyValue items={detailItems(selected)} testId="cash-admin-detail-values" />}
      </DetailPanel>

      {storeId && (
        <ExpenseCategoriesPanel
          storeId={storeId}
          open={categoriesOpen}
          data={categories}
          onChange={setCategories}
          onReload={loadCategories}
          onClose={() => {
            setCategoriesOpen(false);
            // Τα ονόματα των κατηγοριών φαίνονται και στη λίστα.
            load(data?.pagination.page || 1);
          }}
        />
      )}
    </ListPage>
  );
};

export default CashMovementsAdminPage;
